import { useCallback, useMemo } from "react";
import { Socket } from "socket.io-client";
import { useAppContextNonNull } from "@contexts/AppContext";
import EndpointsEnum from "@models/EndpointsEnum";
import IBaseDTO from "@models/DTO/IBaseDTO";
import IGetVendorsDTO from "@models/DTO/IGetVendorsDTO";
import IOnGetVendorsDTO from "@models/DTO/IOnGetVendorsDTO";
import IGetVendorDTO from "@models/DTO/IGetVendorDTO";
import IOnGetVendorDTO from "@models/DTO/IOnGetVendorDTO";
import IGetVendorSourcesDTO from "@models/DTO/IGetVendorSourcesDTO";
import IOnGetVendorSourcesDTO from "@models/DTO/IOnGetVendorSourcesDTO";
import IGetVendorSourceDTO from "@models/DTO/IGetVendorSourceDTO";
import IOnGetVendorSourceDTO from "@models/DTO/IOnGetVendorSourceDTO";
import IGetSelectedCategoryHierarchyDTO from "@models/DTO/IGetSelectedCategoryHierarchyDTO";
import IOnGetSelectedCategoryHierarchyDTO from "@models/DTO/IOnGetSelectedCategoryHierarchyDTO";
import { IFilterSelectOption } from "@models/IFilterConfig";

const REQUEST_TIMEOUT_MS = 10000;

/** callMeta.view stamped on FilterBar lookups so legacy screen handlers can ignore the replies. */
export const FILTER_BAR_CALL_META_VIEW = "FilterBar";

let requestSeq = 0;

/** Unique id for one lookup request, echoed back by endpoints that return the request's callMeta. */
export function nextRequestId(): string {
    requestSeq += 1;

    return `lookup-${requestSeq}`;
}

export interface RequestOnceOptions<T> {
    /**
     * Correlates a reply with this request: only a reply for which it returns true resolves the promise,
     * every other reply on the same event is left for its own request. Without it the first reply wins,
     * which is only safe while the server answers in request order.
     */
    matches?: (data: T) => boolean;
    timeoutMs?: number;
}

/**
 * Emit a socket request and resolve with its reply.
 * Pass `matches` whenever concurrent requests can share `replyEvent` (typeahead lookups, by-id resolvers).
 */
export function requestOnce<T>(
    socket: Socket,
    emitEvent: EndpointsEnum,
    replyEvent: EndpointsEnum,
    payload: unknown,
    options: RequestOnceOptions<T> = {}
): Promise<T> {
    const { matches, timeoutMs = REQUEST_TIMEOUT_MS } = options;

    return new Promise<T>((resolve, reject) => {
        const timeout = setTimeout(() => {
            socket.off(replyEvent, handler);
            reject(new Error(`Timed out waiting for ${replyEvent}`));
        }, timeoutMs);

        const handler = (data: T) => {
            if (matches && !matches(data)) {
                // Another request's reply on the shared event.
                return;
            }

            clearTimeout(timeout);
            socket.off(replyEvent, handler);
            resolve(data);
        };

        socket.on(replyEvent, handler);
        socket.emit(emitEvent, payload);
    });
}

/** Builds the auth portion of a socket DTO for the current user (vendorGuid for vendor users). */
export function useBaseDto(): () => IBaseDTO {
    const { userModel, supplierModel, vendorModel } = useAppContextNonNull();

    return useCallback((): IBaseDTO => {
        const dto: IBaseDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
        };

        if (!userModel.isSupplierUser && vendorModel) {
            dto.vendorGuid = vendorModel.guid;
        }

        return dto;
    }, [userModel, supplierModel, vendorModel]);
}

/**
 * Customer (vendor) typeahead lookup + by-guid resolver, shared by every FilterBar screen config.
 */
export function useVendorLookup() {
    const { socket } = useAppContextNonNull();
    const baseDto = useBaseDto();

    return useMemo(() => {
        // VendorsOnGetVendors does not echo callMeta, so concurrent lookups resolve in reply order;
        // the typeahead field only applies the result of its latest lookup.
        const vendors = (text: string): Promise<IFilterSelectOption[]> => {
            const payload: IGetVendorsDTO = { ...baseDto(), queryString: `name=${encodeURIComponent(text)}` };

            return requestOnce<IOnGetVendorsDTO>(socket, EndpointsEnum.VendorsGetVendors, EndpointsEnum.VendorsOnGetVendors, payload).then(
                (data) =>
                    (data.supplierVendorTokens || [])
                        .map((token) => token.Vendor)
                        .filter((vendor) => !!vendor)
                        .map((vendor) => ({ label: vendor!.name, value: vendor!.guid }))
            );
        };

        const resolveVendor = (guid: string): Promise<IFilterSelectOption | undefined> => {
            const payload: IGetVendorDTO = { ...baseDto(), vendorGuid: guid };

            return requestOnce<IOnGetVendorDTO>(socket, EndpointsEnum.VendorsGetVendor, EndpointsEnum.VendorsOnGetVendor, payload, {
                matches: (data) => !!data.vendor && data.vendor.guid === guid,
            }).then((data) => ({ label: data.vendor.name, value: data.vendor.guid }));
        };

        return { vendors, resolveVendor };
    }, [socket, baseDto]);
}

/**
 * Vendor source typeahead lookup + by-guid resolver (Purchase Orders FilterBar).
 * Tagged with its own callMeta.view so the legacy Purchase Orders typeahead handler ignores the replies.
 */
export function useVendorSourceLookup() {
    const { socket } = useAppContextNonNull();
    const baseDto = useBaseDto();

    return useMemo(() => {
        const vendorSources = (text: string): Promise<IFilterSelectOption[]> => {
            const requestId = nextRequestId();
            const payload: IGetVendorSourcesDTO = {
                ...baseDto(),
                queryString: `name=${encodeURIComponent(text)}`,
                callMeta: { view: FILTER_BAR_CALL_META_VIEW, fn: requestId },
            };

            return requestOnce<IOnGetVendorSourcesDTO>(
                socket,
                EndpointsEnum.PurchaseOrderGetVendorSources,
                EndpointsEnum.PurchaseOrderOnGetVendorSources,
                payload,
                // The API echoes callMeta, so each reply goes to the request that asked for it.
                { matches: (data) => data.callMeta?.fn === requestId }
            ).then((data) => (data.vendorSources || []).map((vendorSource) => ({ label: vendorSource.name, value: vendorSource.guid })));
        };

        const resolveVendorSource = (guid: string): Promise<IFilterSelectOption | undefined> => {
            const payload: IGetVendorSourceDTO = { ...baseDto(), vendorSourceGuid: guid };

            return requestOnce<IOnGetVendorSourceDTO>(
                socket,
                EndpointsEnum.PurchaseOrderGetVendorSource,
                EndpointsEnum.PurchaseOrderOnGetVendorSource,
                payload,
                { matches: (data) => !!data.vendorSource && data.vendorSource.guid === guid }
            ).then((data) => ({ label: data.vendorSource.name, value: data.vendorSource.guid }));
        };

        return { vendorSources, resolveVendorSource };
    }, [socket, baseDto]);
}

/**
 * Category by-guid resolver (Inventory FilterBar): labels a stored category guid with its "Parent > Child"
 * path. The reply does not echo callMeta, so it is matched by the requested guid.
 */
export function useCategoryResolver() {
    const { socket } = useAppContextNonNull();
    const baseDto = useBaseDto();

    return useMemo(() => {
        const resolveCategory = (guid: string): Promise<IFilterSelectOption | undefined> => {
            const payload: IGetSelectedCategoryHierarchyDTO = { ...baseDto(), categoryGuid: guid };

            return requestOnce<IOnGetSelectedCategoryHierarchyDTO>(
                socket,
                EndpointsEnum.CategoriesGetSelectedCategoryHierarchy,
                EndpointsEnum.CategoriesOnGetSelectedCategoryHierarchy,
                payload,
                { matches: (data) => (data.categoryHierarchy || []).some((category) => category.guid === guid) }
            ).then((data) => ({ label: data.categoryHierarchy.map((category) => category.name).join(" > "), value: guid }));
        };

        return { resolveCategory };
    }, [socket, baseDto]);
}
